import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { OrdenServicioConRelaciones } from '@/modules/ordenes-servicio/tipos'

import type { DatosHoja } from './HojaCotizacion'
import { armarHoja } from './useHojaCotizacion'

type CotizacionDeLaObra = {
  id: string
  numero_cotizacion: string | null
  wo_id_asiento: number | null
  wo_id_vendedor: number | null
  es_adicional: boolean | null
}

/**
 * TODA LA OBRA EN UNA HOJA.
 *
 * Johan, sobre «Ver la cotización principal completa»: «no debería ser la
 * cotización principal sino la suma de todas». Una obra es la principal más
 * sus adicionales, y lo que se quiere mirar —o mandar— es el conjunto.
 *
 * Cada documento se arma con la MISMA función de la hoja suelta (armarHoja),
 * así que sus renglones y sus totales son los de su papel. Se juntan en orden
 * —la principal primero— con un rótulo por documento, y el pie suma el pie de
 * cada uno. No es un documento de World Office: lleva el número de la orden y
 * el título «CONSOLIDADO DE LA OBRA», para que nadie lo tome por una
 * cotización con número propio.
 *
 * Entran las que van: ni rechazadas, ni fallidas, ni borradores.
 */
export function useHojaDeLaObra(orden: OrdenServicioConRelaciones) {
  return useQuery({
    queryKey: ['ordenes-servicio', 'hoja-de-la-obra', orden.id],
    queryFn: async (): Promise<{ datos: DatosHoja; documentos: number }> => {
      const { data, error } = await supabase
        .from('cotizaciones')
        .select('id, numero_cotizacion, wo_id_asiento, wo_id_vendedor, es_adicional')
        .eq('orden_servicio_id', orden.id)
        .not('estado', 'in', '(rechazada,fallida,borrador)')
        .order('es_adicional', { ascending: true })
        .order('creado_en', { ascending: true })

      if (error) throw new Error(mensajeDeError(error))
      const cotizaciones = (data ?? []) as unknown as CotizacionDeLaObra[]

      const hojas = await Promise.all(
        cotizaciones.map((c) =>
          armarHoja({
            idAsiento: c.wo_id_asiento,
            cotizacionId: c.id,
            numero: c.numero_cotizacion ?? '',
            prefijo: null,
            fecha: null,
            cliente: orden.cliente?.nombre ?? null,
            clienteDocumento: orden.cliente?.documento ?? null,
            vendedor: null,
            idVendedor: c.wo_id_vendedor,
            direccionObra: orden.direccion ?? null,
            concepto: null,
          }),
        ),
      )

      if (hojas.length === 0) throw new Error('Esta obra no tiene cotizaciones vigentes.')

      const datos: DatosHoja = {
        ...hojas[0],
        titulo: 'CONSOLIDADO DE LA OBRA',
        numero: orden.numero,
        prefijo: 'OS',
        fecha: new Date().toISOString().slice(0, 10),
        // El vendedor de la principal; si no lo tiene, el primero que aparezca.
        vendedor: hojas.find((h) => h.vendedor)?.vendedor ?? null,
        concepto: `Orden de servicio ${orden.numero} · ${cotizaciones.length} documentos`,
        lineas: hojas.flatMap((h, i) => {
          const grupo = `${cotizaciones[i].numero_cotizacion ?? 'Sin número'} · ${
            cotizaciones[i].es_adicional ? 'Adicional' : 'Cotización principal'
          }`
          // Un documento sin renglones no se esconde: se dice, con su rótulo.
          // Solo pasa con datos sembrados a mano, pero desaparecer en
          // silencio haría que el pie no cuadre sin explicación.
          if (h.lineas.length === 0) {
            return [
              {
                codigo: null,
                descripcion: 'Sin renglones registrados en este documento',
                cantidad: 0,
                unidad: '',
                precioUnitario: 0,
                ivaTasa: null,
                ivaValor: 0,
                total: 0,
                grupo,
              },
            ]
          }
          return h.lineas.map((l) => ({ ...l, grupo }))
        }),
        subtotal: hojas.reduce((s, h) => s + h.subtotal, 0),
        iva: hojas.reduce((s, h) => s + h.iva, 0),
        total: hojas.reduce((s, h) => s + h.total, 0),
      }

      return { datos, documentos: cotizaciones.length }
    },
  })
}
